import { useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useImportRedirect } from "@/hooks/useImportRedirect"
import { supabase } from "@/lib/supabase"
import { getSubdomain } from "@/lib/subdomain"
import { pathToSlugs } from "@nos/host-resolution"
import { useSiteStyles, FONT_SIZE_MAP } from "@/hooks/useGoogleFont"
import { useNavigation } from "@/hooks/useNavigation"
import { getTemplateComponent } from "@/templates"
import { siteCssVars } from "@/lib/designTokens"
import { PublicNavBar } from "@/components/PublicNavBar"
import { parseAnnouncement } from "@/lib/announcement"
import { sortPages } from "@/stores/customizationStore"
import { PublicFooter } from "@/components/public/PublicFooter"
import { SEOHead } from "@/components/public/SEOHead"
import { getPublicSiteOrigin } from "@/lib/publicSiteUrl"
import { resolvePageHead } from "@/lib/seo/headData"
import { nonprofitOrganizationJsonLd } from "@/lib/seo/structuredData"
import { BlogListingPage } from "./BlogListingPage"
import { BlogPostPage } from "./BlogPostPage"
import { EventsListingPage } from "./EventsListingPage"
import { EventDetailPage } from "./EventDetailPage"
import { MediaListingPage } from "./MediaListingPage"
import { MediaVideoPage } from "./MediaVideoPage"
import { PublicCampaignDetailPage } from "./CampaignDetailPage"
import { FundraiserSharePage } from "./FundraiserSharePage"
import { CampaignListingPage } from "./CampaignListingPage"
import type { Organization, OrgCustomizations, PageSection } from "@/templates/types"

export { pathToSlugs } from "@nos/host-resolution"

interface PageSummary {
  id: string
  slug: string
  title: string | null
  page_type?: string | null
}

/** NOS-258: nav pages ordered via sortPages() so subdomain and path renderers agree. */
async function fetchNavPages(orgId: string): Promise<PageSummary[] | null> {
  const { data } = await supabase
    .from("pages")
    .select("id, slug, title, page_type")
    .eq("org_id", orgId)
    .eq("is_published", true)
  return data ? sortPages(data as PageSummary[]) : null
}

interface ActivePageSeo {
  slug: string
  title: string | null
  /** NOS-181: SEO overrides (source metadata at import; editable in settings) */
  seo_title: string | null
  seo_description: string | null
  meta: Record<string, unknown>
  updated_at: string
}

/**
 * Public nonprofit site shown on subdomain (e.g. testorg.getnos.ai) or verified custom domain.
 * NOS-65: Custom domain resolves org via `custom_domains` when `getSubdomain()` is null.
 */
export function PublicNonprofitSitePage() {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [org, setOrg] = useState<Organization | null>(null)
  const [customizations, setCustomizations] = useState<OrgCustomizations | null>(null)
  const [sections, setSections] = useState<PageSection[]>([])
  const [pages, setPages] = useState<PageSummary[]>([])
  const [templateName, setTemplateName] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  /** Specific reason for 404: "no-subdomain" | "org-not-found" | "home-not-published" */
  const [notFoundReason, setNotFoundReason] = useState<string | null>(null)
  const [activePageSeo, setActivePageSeo] = useState<ActivePageSeo | null>(null)

  // NOS-241: on a page-level 404, resolve the path against the org's imported
  // redirect map (source URL → new path) and land on the new page instead.
  const redirect = useImportRedirect(
    org?.id,
    pathname,
    notFound && notFoundReason === "page-not-found"
  )
  useEffect(() => {
    if (redirect.status === "found") {
      navigate(redirect.target === "/" ? "/" : redirect.target, { replace: true })
      setNotFound(false)
      setNotFoundReason(null)
    }
  }, [redirect, navigate])

  const { items: navItems } = useNavigation(org?.id ?? null)
  const bodyFont = customizations?.body_font || customizations?.font_family || "Inter, sans-serif"
  const headingFont = customizations?.heading_font || bodyFont
  useSiteStyles(headingFont, bodyFont, customizations?.font_size ?? "medium")

  useEffect(() => {
    let cancelled = false

    async function load() {
      setLoading(true)
      setNotFound(false)
      setNotFoundReason(null)
      setActivePageSeo(null)

      const sub = getSubdomain()
      let orgRow: Organization | null = null

      if (sub) {
        const slug = sub.toLowerCase()
        const { data: orgData, error: orgError } = await supabase
          .from("organizations")
          .select("id, name, slug, mission_statement, logo_url, custom_domain, podcast_settings, legal_name, mailing_address, founding_year")
          .eq("slug", slug)
          .maybeSingle()

        if (cancelled || orgError || !orgData) {
          if (!cancelled) {
            setOrg(null)
            setNotFound(true)
            setNotFoundReason("org-not-found")
            setLoading(false)
          }
          return
        }
        orgRow = orgData as Organization
      } else {
        const hostname = window.location.hostname.toLowerCase()
        if (hostname === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
          if (!cancelled) {
            setOrg(null)
            setNotFound(true)
            setNotFoundReason("no-subdomain")
            setLoading(false)
          }
          return
        }
        if (hostname === "app.getnos.ai" || hostname.startsWith("app.")) {
          if (!cancelled) {
            setOrg(null)
            setNotFound(true)
            setNotFoundReason("no-subdomain")
            setLoading(false)
          }
          return
        }

        const candidates = [hostname]
        if (hostname.startsWith("www.")) candidates.push(hostname.slice(4))
        else candidates.push(`www.${hostname}`)

        let orgId: string | null = null
        for (const domain of candidates) {
          const { data: row } = await supabase
            .from("custom_domains")
            .select("org_id")
            .eq("domain", domain)
            .eq("verification_status", "verified")
            .maybeSingle()
          if (row?.org_id) {
            orgId = row.org_id
            break
          }
        }

        if (!orgId) {
          if (!cancelled) {
            setOrg(null)
            setNotFound(true)
            setNotFoundReason("org-not-found")
            setLoading(false)
          }
          return
        }

        const { data: orgData, error: orgError } = await supabase
          .from("organizations")
          .select("id, name, slug, mission_statement, logo_url, custom_domain, podcast_settings, legal_name, mailing_address, founding_year")
          .eq("id", orgId)
          .maybeSingle()

        if (cancelled || orgError || !orgData) {
          if (!cancelled) {
            setOrg(null)
            setNotFound(true)
            setNotFoundReason("org-not-found")
            setLoading(false)
          }
          return
        }
        orgRow = orgData as Organization
      }

      if (!orgRow) {
        if (!cancelled) setLoading(false)
        return
      }

      if (!cancelled) setOrg(orgRow)

      const { data: custData, error: custErr } = await supabase
        .from("org_customizations")
        .select("primary_color, secondary_color, font_family, heading_font, body_font, font_size, sticky_nav, template_id, template_version, nav_cta_text, custom_css, logo_scale, announcement, design_tokens, footer_content")
        .eq("org_id", orgRow.id)
        .maybeSingle()

      if (cancelled) return
      const cust = custErr ? null : (custData as OrgCustomizations | null)
      if (cust) setCustomizations(cust)

      let name: string | null = null
      if (cust?.template_id) {
        const { data: templateData } = await supabase
          .from("templates")
          .select("name")
          .eq("id", cust.template_id)
          .maybeSingle()
        if (templateData && typeof (templateData as { name: string }).name === "string") {
          name = (templateData as { name: string }).name
        }
      }
      if (!cancelled) setTemplateName(name ?? null)

      const isBlogRoute =
        pathname === "/blog" || Boolean(pathname.match(/^\/blog\/[^/]+$/))
      const isEventsRoute =
        pathname === "/events" || Boolean(pathname.match(/^\/events\/[^/]+$/))
      const isMediaRoute =
        pathname === "/media" || Boolean(pathname.match(/^\/media\/[^/]+$/))
      const isCampaignRoute =
        pathname === "/campaign" ||
        Boolean(pathname.match(/^\/campaign\/[^/]+(\/fundraise\/[^/]+)?$/))
      if (isCampaignRoute || isMediaRoute || isBlogRoute || isEventsRoute) {
        const pagesData = await fetchNavPages(orgRow.id)
        if (!cancelled && pagesData) setPages(pagesData)
        if (!cancelled) {
          setSections([])
          setLoading(false)
        }
        return
      }

      interface LoadedPageRow {
        id: string
        slug: string
        title: string | null
        seo_title: string | null
        seo_description: string | null
        meta: unknown
        updated_at: string
      }

      const pageSlugsToTry = pathToSlugs(pathname)
      let pageData: LoadedPageRow | null = null
      for (const pageSlug of pageSlugsToTry) {
        const { data, error } = await supabase
          .from("pages")
          .select("id, slug, title, seo_title, seo_description, meta, updated_at")
          .eq("org_id", orgRow.id)
          .eq("slug", pageSlug)
          .eq("is_published", true)
          .maybeSingle()
        if (!cancelled && !error && data) {
          pageData = data as LoadedPageRow
          break
        }
      }

      if (cancelled) return

      if (!pageData) {
        if (!cancelled) {
          setNotFound(true)
          setNotFoundReason(pageSlugsToTry[0] === "home" ? "home-not-published" : "page-not-found")
          setLoading(false)
        }
        return
      }

      const pageId = pageData.id
      const metaObj =
        pageData.meta && typeof pageData.meta === "object" && !Array.isArray(pageData.meta)
          ? (pageData.meta as Record<string, unknown>)
          : {}
      if (!cancelled) {
        setActivePageSeo({
          slug: pageData.slug,
          title: pageData.title,
          seo_title: pageData.seo_title,
          seo_description: pageData.seo_description,
          meta: metaObj,
          updated_at: pageData.updated_at,
        })
      }

      const { data: sectionsData, error: sectionsError } = await supabase
        .from("page_sections")
        .select("id, section_type, order_index, content, page_id, org_id, is_visible")
        .eq("page_id", pageId)
        .order("order_index", { ascending: true })

      const pagesData = await fetchNavPages(orgRow.id)

      if (cancelled) return
      if (pagesData) setPages(pagesData)
      if (!sectionsError && sectionsData) {
        setSections(sectionsData as PageSection[])
      } else {
        setSections([])
      }
      if (!cancelled) setLoading(false)
    }

    load()
    return () => {
      cancelled = true
    }
  }, [pathname])

  const isBlogRoute =
    pathname === "/blog" || Boolean(pathname.match(/^\/blog\/[^/]+$/))
  const blogPostSlug = pathname.match(/^\/blog\/([^/]+)$/)?.[1]
  const isEventsRoute =
    pathname === "/events" || Boolean(pathname.match(/^\/events\/[^/]+$/))
  const eventSlug = pathname.match(/^\/events\/([^/]+)$/)?.[1]
  const isMediaRoute =
    pathname === "/media" || Boolean(pathname.match(/^\/media\/[^/]+$/))
  const mediaVideoSlug = pathname.match(/^\/media\/([^/]+)$/)?.[1]
  const isCampaignRoute =
    pathname === "/campaign" ||
    Boolean(pathname.match(/^\/campaign\/[^/]+(\/fundraise\/[^/]+)?$/))

  // NOS-249: title/description/canonical/og:image resolution moved into the
  // shared renderer-agnostic resolver (src/lib/seo/headData.ts). Pages
  // without an explicit meta.og_image get the generated branded default.
  const headData = useMemo(() => {
    if (!org) return null
    return resolvePageHead({
      org,
      page: activePageSeo,
      sitePath: pathname === "" ? "/" : pathname,
      sections,
    })
  }, [org, activePageSeo, pathname, sections])

  // NOS-242: regenerated NonprofitOrganization JSON-LD (see structuredData.ts).
  // EIN intentionally omitted — no public-display opt-in exists yet (M25
  // sponsor/transfer contexts could leak a wrong-entity taxID).
  const jsonLd = useMemo(() => {
    if (!org || !activePageSeo) return []
    if (activePageSeo.slug !== "home") return []
    return [
      nonprofitOrganizationJsonLd({
        name: org.name,
        url: getPublicSiteOrigin(org),
        logoUrl: org.logo_url,
        missionStatement: org.mission_statement,
        legalName: org.legal_name,
        address: org.mailing_address,
        foundingYear: org.founding_year,
      }),
    ]
  }, [org, activePageSeo])

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    )
  }

  if (isCampaignRoute && org) {
    if (pathname === "/campaign") {
      return (
        <CampaignListingPage
          orgSlug={org.slug}
          org={org}
          customizations={customizations}
          basePath=""
        />
      )
    }
    const fundraiseMatch = pathname.match(/^\/campaign\/([^/]+)\/fundraise\/([^/]+)\/?$/)
    if (fundraiseMatch) {
      const [, cSlug, token] = fundraiseMatch
      return (
        <FundraiserSharePage
          orgSlug={org.slug}
          campaignSlug={cSlug}
          shareToken={token}
          org={org}
          customizations={customizations}
          basePath=""
        />
      )
    }
    const detailMatch = pathname.match(/^\/campaign\/([^/]+)\/?$/)
    if (detailMatch) {
      const [, cSlug] = detailMatch
      return (
        <PublicCampaignDetailPage
          orgSlug={org.slug}
          campaignSlug={cSlug}
          org={org}
          customizations={customizations}
          basePath=""
        />
      )
    }
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center px-4 max-w-md">
          <h1 className="text-4xl font-bold text-gray-900">404</h1>
          <p className="mt-2 text-gray-600">This page doesn’t exist or hasn’t been published yet.</p>
          <a href="/campaign" className="mt-4 inline-block text-sm text-blue-600 underline">
            View campaigns
          </a>
        </div>
      </div>
    )
  }

  if (isMediaRoute && org) {
    if (pathname === "/media") {
      return (
        <MediaListingPage
          orgSlug={org.slug}
          org={org}
          customizations={customizations}
          basePath=""
        />
      )
    }
    if (mediaVideoSlug) {
      return (
        <MediaVideoPage
          orgSlug={org.slug}
          videoSlug={mediaVideoSlug}
          org={org}
          customizations={customizations}
          basePath=""
        />
      )
    }
  }

  if (isBlogRoute && org) {
    if (pathname === "/blog") {
      return (
        <BlogListingPage
          orgSlug={org.slug}
          org={org}
          customizations={customizations}
          basePath=""
        />
      )
    }
    if (blogPostSlug) {
      return (
        <BlogPostPage
          orgSlug={org.slug}
          postSlug={blogPostSlug}
          org={org}
          customizations={customizations}
          basePath=""
        />
      )
    }
  }

  if (isEventsRoute && org) {
    if (pathname === "/events") {
      return (
        <EventsListingPage
          orgSlug={org.slug}
          org={org}
          customizations={customizations}
          basePath=""
        />
      )
    }
    if (eventSlug) {
      return (
        <EventDetailPage
          orgSlug={org.slug}
          eventSlug={eventSlug}
          org={org}
          customizations={customizations}
          basePath=""
        />
      )
    }
  }

  if (notFound || !org) {
    // NOS-241: while the redirect map is being consulted, hold the 404 — a
    // stale imported URL should land on its new page without a 404 flash.
    if (redirect.status === "checking") {
      return (
        <div className="flex min-h-screen items-center justify-center">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      )
    }
    const messages: Record<string, string> = {
      "no-subdomain": "No subdomain detected. Make sure you’re visiting your site at the correct URL.",
      "org-not-found": "Organization not found. Check that the subdomain matches your organization’s slug.",
      "home-not-published":
        "Home page is not published yet. Open the website builder, select the Home page, and click Publish.",
      "page-not-found": "This page doesn’t exist or hasn’t been published yet.",
    }
    const detail = notFoundReason ? messages[notFoundReason] : "Organization not found."
    const showHomeLink = notFoundReason === "page-not-found"
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center px-4 max-w-md">
          <h1 className="text-4xl font-bold text-gray-900">404</h1>
          <p className="mt-2 text-gray-600">{detail}</p>
          {showHomeLink && (
            <a href="/" className="mt-4 inline-block text-sm text-blue-600 underline">
              Go to home page
            </a>
          )}
        </div>
      </div>
    )
  }

  const defaults: OrgCustomizations = {
    primary_color: "#3b82f6",
    secondary_color: "#1e40af",
    font_family: "Inter, sans-serif",
    template_id: null,
  }
  const cust = customizations ?? defaults
  const TemplateComponent = getTemplateComponent(templateName)
  const primaryColor = cust.primary_color ?? "#3b82f6"
  const navFontFamily = cust.body_font || cust.font_family || "Inter, sans-serif"
  const custFontSizeBase = FONT_SIZE_MAP[cust.font_size ?? "medium"] ?? "16px"

  /** Path for internal page links on subdomain (e.g. /about, /) */
  const getPagePath = (slug: string) => (slug === "home" ? "/" : `/${slug.replace(/^\//, "")}`)

  const siteContent = (
    <div
      className="nos-site-root"
      style={{
        all: "initial",
        fontFamily: navFontFamily,
        fontSize: custFontSizeBase,
        display: "block",
        ...siteCssVars(cust),
        ["--primary-color" as string]: primaryColor,
        ["--secondary-color" as string]: cust.secondary_color ?? "#1e40af",
        ["--font-family" as string]: navFontFamily,
      }}
    >
      {headData ? (
        <SEOHead
          title={headData.title}
          description={headData.description}
          canonicalUrl={headData.canonicalUrl}
          ogType={headData.ogType}
          imageUrl={headData.ogImage}
          jsonLd={jsonLd.length ? jsonLd : null}
        />
      ) : null}
      {cust.custom_css ? <style>{cust.custom_css}</style> : null}
      <PublicNavBar
        orgName={org.name ?? "Organization"}
        logoUrl={org.logo_url}
        navItems={navItems}
        pages={pages}
        primaryColor={primaryColor}
        navCtaText={cust.nav_cta_text ?? "Donate"}
        announcement={parseAnnouncement(cust.announcement)}
        fontFamily={navFontFamily}
        getPagePath={getPagePath}
        orgId={org.id}
        logoScale={(cust.logo_scale as number | undefined) ?? 1.0}
        stickyNav={cust.sticky_nav ?? true}
      />
      <TemplateComponent org={org} customizations={cust} sections={sections} />
      <PublicFooter
        orgId={org.id}
        orgName={org.name ?? "Organization"}
        logoUrl={org.logo_url}
        missionStatement={org.mission_statement}
        primaryColor={primaryColor}
        secondaryColor={cust.secondary_color ?? "#1e40af"}
        fontFamily={navFontFamily}
        pages={pages}
        getPagePath={getPagePath}
        logoScale={(cust.logo_scale as number | undefined) ?? 1.0}
      />
    </div>
  )

  return (
    <div
      className="min-h-screen scroll-smooth"
      style={
        {
          "--primary-color": primaryColor,
          "--secondary-color": cust.secondary_color ?? "#1e40af",
          "--font-family": cust.font_family ?? "Inter, sans-serif",
          fontFamily: navFontFamily,
        } as React.CSSProperties
      }
    >
      {siteContent}
    </div>
  )
}
